import { END_POINTS } from "constants/APIConstant";

export const createExportedExcelFileName = ({
  fileIndex = 0,
  totalFiles = 1,
} = {}) => {
  const suffix = totalFiles > 1 ? `-${fileIndex + 1}` : "";
  return `Exported_Data${suffix}.xlsx`;
};

export const extractExcelUrlsFromText = (text) => {
  if (!text || typeof text !== "string") return [];
  const urls = [];
  const linkRe = /\[([^\]]*)\]\s*\(([^)]+)\)/g;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    const label = (m[1] || "").trim().toUpperCase();
    const href = (m[2] || "").replace(/\s+/g, "").trim();
    if (!href) continue;
    const pathOnly = href.split(/[?#]/)[0];
    const isExcelLabel = label === "EXCEL";
    const isXlsxHref = /\.xlsx$/i.test(pathOnly);
    if (!isExcelLabel && !isXlsxHref) continue;
    urls.push(href);
  }
  return Array.from(new Set(urls));
};

export const stripExcelLinksFromText = (text) => {
  if (!text || typeof text !== "string") return text || "";
  return text
    .replace(/\[\s*EXCEL\s*\]\s*\(([^)]+)\)/gi, "")
    .replace(/\(([^)]+\.xlsx)\)\s*\[\s*EXCEL\s*\]/gi, "")
    .trim();
};

export const extractGraphUrlsFromText = (text) => {
  if (!text || typeof text !== "string") return [];
  const urls = [];
  const graphLinkRe = /\[\s*GRAPH\s*\]\s*\(([^)]+)\)/gi;
  let m;
  while ((m = graphLinkRe.exec(text)) !== null) {
    const href = (m[1] || "").replace(/\s+/g, "").trim();
    if (!href) continue;
    const pathOnly = href.split(/[?#]/)[0];
    if (!/\.html$/i.test(pathOnly)) continue;
    urls.push(href);
  }
  return Array.from(new Set(urls));
};

export const stripGraphLinksFromText = (text) => {
  if (!text || typeof text !== "string") return text || "";
  return text.replace(/\[\s*GRAPH\s*\]\s*\(([^)]+)\)/gi, "").trim();
};

export function normalizeGraphHrefToUrl(hrefRaw) {
  let href = (hrefRaw || "").replace(/\s+/g, "").trim();
  if (!href) return null;

  const pathOnly = href.split(/[?#]/)[0];
  if (!/\.html$/i.test(pathOnly)) return null;

  if (/^https?:\/\//i.test(href)) return href;

  const fileName = href.replace(/^\/+/, "");
  const base = String(END_POINTS.CHATBOT_API_BASE_URL_GRAPH || "").replace(
    /\/+$/,
    "",
  );
  return `${base}/${fileName}`;
}

export function normalizeExcelHrefToUrl(hrefRaw) {
  let href = (hrefRaw || "").replace(/\s+/g, "").trim();
  if (!href) return null;

  const pathOnly = href.split(/[?#]/)[0];
  if (!/\.xlsx$/i.test(pathOnly)) return null;

  if (/^https?:\/\//i.test(href)) return href;

  const fileUrl = href.replace(/^\/+/, "");
  const base = String(END_POINTS.CHATBOT_API_BASE_URL_FILE || "").replace(
    /\/+$/,
    "",
  );
  return `${base}/${fileUrl}`;
}

/**
 * Split assistant text so GRAPH/EXCEL tokens become ordered media segments.
 */
export function splitIntoInlineMediaSegments(text) {
  if (!text || typeof text !== "string") {
    return [{ type: "markdown", content: text || "" }];
  }

  const rawTokens = [];

  const pushMatches = (re, type) => {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      rawTokens.push({
        type,
        index: m.index,
        end: m.index + m[0].length,
        href: (m[1] || "").replace(/\s+/g, "").trim(),
      });
    }
  };

  pushMatches(/\[\s*GRAPH\s*\]\s*\(([^)]+)\)/gi, "graph");
  pushMatches(/\[\s*EXCEL\s*\]\s*\(([^)]+)\)/gi, "excel");
  pushMatches(/\[[^\]]*\]\s*\(([^)]+\.xlsx)\)/gi, "excel");

  rawTokens.sort((a, b) => a.index - b.index || b.end - a.end);

  const tokens = [];
  const seenRange = new Set();
  let lastEnd = -1;
  for (const t of rawTokens) {
    const rangeKey = `${t.index}:${t.end}`;
    if (seenRange.has(rangeKey)) continue;
    if (t.index < lastEnd) continue;
    seenRange.add(rangeKey);
    tokens.push(t);
    lastEnd = t.end;
  }

  const segments = [];
  let cursor = 0;
  for (const t of tokens) {
    if (t.index > cursor) {
      segments.push({ type: "markdown", content: text.slice(cursor, t.index) });
    }
    segments.push({ type: t.type, href: t.href });
    cursor = t.end;
  }
  if (cursor < text.length) {
    segments.push({ type: "markdown", content: text.slice(cursor) });
  }

  return segments.length ? segments : [{ type: "markdown", content: text }];
}

/**
 * Collect normalized graph/excel URLs and a text body with tokens stripped.
 */
export function extractSharedMedia(answerText) {
  const text = String(answerText || "");
  const segments = splitIntoInlineMediaSegments(text);
  const hasInlineMedia = segments.some(
    (s) => s.type === "graph" || s.type === "excel",
  );

  const graphUrls = [];
  const excelUrls = [];
  const media = [];

  if (hasInlineMedia) {
    for (const seg of segments) {
      if (seg.type === "markdown") {
        const content = String(seg.content || "").trim();
        if (content) media.push({ type: "html", content });
        continue;
      }
      if (seg.type === "graph") {
        const url = normalizeGraphHrefToUrl(seg.href);
        if (url) {
          graphUrls.push(url);
          media.push({ type: "graph", url });
        }
        continue;
      }
      if (seg.type === "excel") {
        const url = normalizeExcelHrefToUrl(seg.href);
        if (url) {
          excelUrls.push(url);
          media.push({ type: "excel", url });
        }
      }
    }
  } else {
    extractGraphUrlsFromText(text).forEach((href) => {
      const url = normalizeGraphHrefToUrl(href);
      if (url) {
        graphUrls.push(url);
        media.push({ type: "graph", url });
      }
    });
    extractExcelUrlsFromText(text).forEach((href) => {
      const url = normalizeExcelHrefToUrl(href);
      if (url) {
        excelUrls.push(url);
        media.push({ type: "excel", url });
      }
    });
    const stripped = stripExcelLinksFromText(stripGraphLinksFromText(text));
    if (stripped.trim()) media.unshift({ type: "html", content: stripped });
  }

  const textWithoutMedia = stripExcelLinksFromText(
    stripGraphLinksFromText(text),
  );

  return {
    textWithoutMedia,
    graphUrls: Array.from(new Set(graphUrls)),
    excelUrls: Array.from(new Set(excelUrls)),
    media,
    hasInlineMedia,
  };
}
